/* eslint "no-useless-escape": "off" */
import cx from 'classnames';
import findLastKey from 'lodash/findLastKey';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import { getLanguage } from 'actions/localization';
import { CHANNEL_PAY_LATER } from 'consts/loan/channels';
import queryStringParser from 'utils/queryStringParser';

const userAgent = navigator.userAgent.toLowerCase();

export const getAndroidVersion = () => {
  const check = userAgent.match(/android\s([0-9\.]*)/);
  return check ? check[1] : false;
};

export const isChrome = () => userAgent.indexOf('chrome') > -1;

export const getMmpFromMonthArray = (months, sum) => {
  const monthsArray = Object.values(months);
  return monthsArray.find((month) => (month.sum === sum)).mmp;
};

export const constructErrorMessage = (customerId, errorResponse) => {
  const {
    config: { url, method },
    status: errCode,
    headers,
  } = errorResponse;
  const spanId = 'x-b3-spanid' in headers ? `, X-B3-SpanId = ${headers['x-b3-spanid']}` : '';
  const msg = `${method.toUpperCase()} ${url} failed, custId = ${customerId}${spanId}`;
  return { ID: null, errCode, msg };
};

const removeDigitCharacters = (value) => (value ? value.toString().replace(/[\d]+/g, '') : '');
export const removeNonDigitCharacters = (value) => ((value || value === 0) ? value.toString().replace(/[\D]+/g, '') : '');
// International letter & number matcher inverted - https://regexr.com/3r3lc
export const removeSymbolCharacters = (value, removeSpaces = false, exceptionsArray = []) => {
  let exceptions = '';
  if (isArray(exceptionsArray)) {
    exceptions = exceptionsArray.join('');
  }
  const regex = new RegExp(`[^\u00C0-\u1FFF\u2C00-\uD7FFa-z0-9${removeSpaces ? '' : ' '}${exceptions}]+`, 'ig');
  return value ? value.toString().replace(regex, '') : '';
};
export const normalizePhoneNumber = (value) => (
  value ? value.toString().replace(/[^+\d]/g, '').replace(/(?!^)\+/g, '') : ''
);
export const removeSpaces = (value) => (value ? value.replace(/ /g, '') : '');
export const removeNonEnglishCharacters = (value) => (
  value ? removeDigitCharacters(value).replace(/^[-]+|[^a-z '-]+/ig, '') : ''
);
export const capitalizeString = (value) => {
  if (value && value.length > 0) {
    return value[0].toUpperCase() + value.slice(1);
  }
  return value || '';
};

// Remove digits, symbols & auto-capitalize first word
export const normalizeName = (value) => capitalizeString(
  removeSymbolCharacters(removeDigitCharacters(value))
);

export const removeSpecialCharacters = (value) => value.replace(/[!@#$%^&*()+{}[\]|:;"<>,.\/?`~_=\\ 0-9]+/g, '');

export const getConsentsFormInitialValues = (consents) => (
  consents.reduce(
    (result, consent) => {
      const {
        consentName,
        value,
        dependantConsents,
      } = consent;
      return {
        ...result,
        ...(dependantConsents
          ? getConsentsFormInitialValues(dependantConsents) : null),
        [consentName]: value,
      };
    },
    []
  )
);

export const toCents = (value, type = 'int') => {
  const factor = 100;
  if (type === 'float') {
    let cleanedValue = value;
    if (value) {
      cleanedValue = value.toString().replace(/[^\d.,]+/g, '');
    }
    return Math.round(parseFloat(cleanedValue) * factor);
  }
  return parseInt(removeNonDigitCharacters(value), 10) * factor;
};

export const fromCents = (value, type = 'int') => {
  if (typeof value === 'undefined') {
    return;
  }
  const factor = 100;
  if (type === 'float') {
    return parseFloat(value) / factor;
  }
  return parseInt(value, 10) / factor;
};

export const getMMP = (principal, period, rate) => {
  const r = (rate / 100);
  /* eslint no-mixed-operators: "off" */
  return (r / (1 - (1 + r) ** -period) * principal);
};

export const toUpper = (value) => (value ? value.toUpperCase() : '');

export const commaToPeriod = (value) => (value ? value.replace(',', '.') : '');
export const removeNonDigitCharactersKeepDecimals = (value) => (
  value ? value.toString().replace(',', '.').replace(/[^0-9\.]+/g, '') : ''
);

const normalizeMsisdnEE = (phone) => {
  let msisdn = phone;
  const match = msisdn.match(/^0+(\d+)$/);
  if (match) {
    msisdn = match[1];
  }
  if (msisdn.startsWith('372')) {
    msisdn = `+${msisdn}`;
  }
  if (!msisdn.startsWith('+372')) {
    msisdn = `+372${msisdn}`;
  }
  return msisdn;
};

const normalizeMsisdnAU = (phone) => {
  const msisdn = removeNonDigitCharacters(phone);
  return msisdn.replace(/^0/, '+61');
};

export const normalizeMsisdn = (phone) => {
  switch (COUNTRY) {
    case 'EE':
      return normalizeMsisdnEE(phone);
    case 'AU':
      return normalizeMsisdnAU(phone);
    case 'LV':
      return removeNonDigitCharacters(phone);
    case 'ES':
    default:
      return `+${removeNonDigitCharacters(phone)}`;
  }
};

export const normalizeSsnMX = (ssn) => (ssn ? toUpper(ssn).substr(0, 18) : '');

export const normalizeLoanPurpose = (purpose) => (removeDigitCharacters(purpose).substr(0, 30));

const formatMsisdnCZ = (phone) => phone.replace(/(\d{3})(\d{3})(\d{3})(\d)/, '$1 $2 $3 $4');

export const formatMsisdnAU = (phone) => phone.replace('+61', '0');

export const formatMsisdn = (phone) => {
  if (phone) {
    switch (COUNTRY) {
      case 'CZ':
        return formatMsisdnCZ(phone);
      default:
        return phone.replace(/(\d{3})(\d{2})(\d{3})(\d)/, '$1 $2 $3 $4');
    }
  } else {
    return '';
  }
};

export const formatIban = (iban) => {
  if (iban) {
    return iban.replace(/(\d{2})(\d{4})(\d{4})(\d{4})(\d)/, '$1 $2 $3 $4 $5');
  }
  return '';
};

export const formatCustomerNumberFI = (customerNumber) => (
  customerNumber ? removeNonDigitCharacters(customerNumber).substr(0, 8) : ''
);

export const formatCustomerNumberCZ = (customerNumber) => (
  customerNumber ? removeNonDigitCharacters(customerNumber).substr(0, 8) : ''
);

const getTotalSum = (total = 0, value = 0, validation) => {
  if (!validation || validation(value)) {
    return total + (parseInt(value, 10) || 0);
  }
  return total;
};

export const calculateSumForArrayElements = (array, validation) => (
  isArray(array) ? array.reduce((total, value) => getTotalSum(total, value, validation), 0) : 0
);

export const calculateSumFromObjectProps = (object, validation) => (
  isObject(object)
    ? Object.keys(object).reduce((total, key) => getTotalSum(total, object[key], validation), 0)
    : 0
);

export const cleanPropsAndCalculateTotalSum = (object, validation) => (
  isObject(object)
    ? Object.keys(object).reduce((total, key) =>
      getTotalSum(total, removeNonDigitCharacters(object[key]), validation), 0)
    : 0
);

export const isMobile = (
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
    .test(navigator.userAgent)
);

export const getInitialProductPrincipal = (price, product) => {
  if (price > product.maxPrincipal) {
    return product.maxPrincipal;
  }
  if (price < product.minPrincipal) {
    return product.minPrincipal;
  }
  return Math.ceil(price / product.step) * product.step;
};

const getCurrencyCodeFromCountryCode = () => {
  switch (COUNTRY) {
    case 'MX':
      return 'MXN';
    case 'PL':
      return 'PLN';
    case 'AU':
      return 'AUD';
    case 'CZ':
      return 'CZK';
    default:
      return 'EUR';
  }
};

let currency = null;
export const getCurrencyCode = () => {
  if (!currency) {
    currency = getCurrencyCodeFromCountryCode();
  }
  return currency;
};

// Validations

export const isNullOrEmpty = (value) => !value || value === 'empty';
export const keyErrorsFieldRequired = 'errors.field.required';

export const fieldLevelCheckboxValidator = [
  (value) => (value ? undefined : keyErrorsFieldRequired),
];

export const externalScriptPromise = (url, id = null, allowDuplicates = false) => (
  new Promise((resolve, reject) => {
    /* eslint consistent-return: "off" */
    if (!allowDuplicates && document.getElementById(id)) {
      return resolve();
    }
    let done = false;
    const target = document.getElementsByTagName('script')[0];
    const script = document.createElement('script');

    script.type = 'text/javascript';
    script.src = url;
    if (id) {
      script.id = id;
    }
    script.async = true;
    script.onload = script.onreadystatechange = () => {
      if (!done && (!script.readyState
        || script.readyState === 'complete'
        || script.readyState === 'loaded')) {
        done = true;

        script.onload = script.onreadystatechange = null;
        resolve(script);
      }
    };
    script.onerror = script.onabort = () => {
      script.parentNode.removeChild(script);
      reject(`Failed to load resource: ${url}`);
    };
    target.parentNode.insertBefore(script, target);
  })
);

export const addGtmScript = (customGtmIdentifier) => {
  const id = customGtmIdentifier || GTM_CONTAINER_ID;

  if (id && !document.getElementById(id)) {
    const gtmScript = document.createElement('script');
    gtmScript.id = id;
    gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${id}');`;

    const antiFlickerStyle = document.createElement('style');
    antiFlickerStyle.innerHTML = '.async-hide { opacity: 0 !important}';
    const antiFlickerScript = document.createElement('script');
    antiFlickerScript.innerHTML = `(function(a,s,y,n,c,h,i,d,e){s.className+=' '+y;h.start=1*new Date;
    h.end=i=function(){s.className=s.className.replace(RegExp(' ?'+y),'')};
    (a[n]=a[n]||[]).hide=h;setTimeout(function(){i();h.end=null},c);h.timeout=c;
    })(window,document.documentElement,'async-hide','dataLayer',4000,
    {'${id}':true});`;

    document.head.appendChild(gtmScript);
    document.head.appendChild(antiFlickerStyle);
    document.head.appendChild(antiFlickerScript);
  }
};

// Solution #1 from https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding
export const b64DecodeUnicode = (str = '') => (
  decodeURIComponent(atob(str).split('').map((c) => (
    `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)).join(''))
);

export const getRootURL = (locale, country = COUNTRY) => {
  const lowercaseCountry = country.toLowerCase();
  let link = `/${lowercaseCountry}/`;
  const domain = window.location.href;
  const isRebranding = domain.includes('creditea.com');
  const countriesWithOwnDomain = ['es', 'pl', 'cz', 'lv', 'au', 'mx', 'fi', 'lt'];
  if (lowercaseCountry === 'ee' && (!isRebranding || !process.env.REBRANDING)) {
    link = 'https://www.credit24.ee/';
  }
  if (lowercaseCountry === 'ee' && (isRebranding || process.env.REBRANDING)) {
    link = 'https://www.creditea.com/ee/';
  }
  if (countriesWithOwnDomain.includes(lowercaseCountry)) {
    link = '/';
  }
  if (locale === 'ru') {
    if (lowercaseCountry === 'lv') {
      link = `${link}?lang=ru`;
    } else {
      link = `${link}ru/`;
    }
  }
  return link;
};

export const getInvoicingMaturityKeys = (maturityLength) => {
  switch (maturityLength) {
    case 'TWO_WEEKS':
      return 'invoicingFeeFortnightly';
    case 'ONE_WEEK':
      return 'invoicingFeeWeekly';
    case 'ONE_MONTH':
    default:
      return 'invoicingFeeMonthly';
  }
};

export const getCreditInfoMaturityKeys = (maturityLength) => {
  switch (maturityLength) {
    case 'TWO_WEEKS':
      return 'selfservice.creditInformation.mfp';
    case 'ONE_WEEK':
      return 'selfservice.creditInformation.mwp';
    case 'ONE_MONTH':
    default:
      return 'selfservice.creditInformation.mmp';
  }
};

export const getMinimumFractionDigits = (value, minimumFractionDigits) => (
  (value % 1 === 0 && value !== 0) ? 0 : minimumFractionDigits
);

/* eslint-disable max-len */
export const msisdnFIRegExp = /^((90[0-9]{3})?0|\+358\s?)(?!(100|20(0|2(0|[2-3])|9[8-9])|300|600|700|708|75(00[0-3]|(1|2)\d{2}|30[0-2]|32[0-2]|75[0-2]|98[0-2])))(4|50|10[1-9]|20(1|2(1|[4-9])|[3-9])|29|30[1-9]|71|73|75(00[3-9]|30[3-9]|32[3-9]|53[3-9]|83[3-9])|2|3|5|6|8|9|1[3-9])\s?(\d\s?){4,19}\d$/;
/* eslint-enable */

export const generateConsent = (consentName, value, version) => ({
  consentName,
  value,
  version,
  validUntil: '2999-12-31',
});

export function getErrorCode(response) {
  if (response.data && response.data.errCode) {
    return response.data.errCode;
  }
  if (response.data && response.status) {
    return response.status;
  }

  return null;
}

export const generateConsents = (consents, data, locale = '') => (
  consents.reduce(
    (result, consent) => {
      const {
        consentName,
        dependantConsents = [],
        localizedVersions = {},
      } = consent;
      const consentVersion = localizedVersions[locale] || consent.version;
      const value = data[consentName] || false;
      const additionalConsents = dependantConsents.map(
        ({ consentName: dependantConsentName }) => (
          generateConsent(dependantConsentName, value, consentVersion)
        )
      );
      return [
        ...result,
        ...additionalConsents,
        generateConsent(consentName, value, consentVersion),
      ];
    },
    []
  )
);

export const creditApplicationInitPath = () => {
  switch (COUNTRY) {
    case 'AU':
    case 'ES':
    case 'MX':
    case 'PL':
    case 'CZ':
      return `credit-application-${COUNTRY.toLowerCase()}`;
    default:
      return 'credit-application';
  }
};

export const getInvoiceAmount = (invoice) => (
  !invoice.closeDate ? invoice.amount - invoice.allocatedAmount : invoice.amount
);

export const getHiddenFieldsClassNames = (condition) => (
  cx({
    'grid__item one-half portable-one-whole': true,
    hide: !condition,
  })
);

export const isBrokerChannel = (channel) => channel === 'broker_app' || channel === 'Hybrid';
export const isPayLaterChannel = (channel) => channel === CHANNEL_PAY_LATER;

export function getRedirectUrlSS() {
  const country = COUNTRY.toLowerCase();
  let appUrl = 'application';
  if (COUNTRY === 'FI' || COUNTRY === 'AU' || COUNTRY === 'CZ') {
    appUrl = 'self-service';
  }
  if (COUNTRY === 'LT') {
    appUrl = 'application';
  }
  let url = `/${country}/${appUrl}/`;
  if (COUNTRY === 'EE' && isMobile) {
    url = `/mobile/?platform=web&country=ee&language=ee_${getLanguage()}&load=1`;
  }
  return url;
}

export const getMMPFromMMPsByMonth = (mmpsByMonth, selectedProduct) => {
  const isLatvianCL = COUNTRY === 'LV' && selectedProduct.type === 'CREDIT_LINE';
  const maturityPeriod = isLatvianCL ? 60 : 36;
  let maxMaturity = mmpsByMonth
    ? mmpsByMonth.filter((item) => item.maturityPeriod === maturityPeriod)
    : null;
  maxMaturity = maxMaturity
    ? maxMaturity.map((item) => item.data)
    : null;
  maxMaturity = maxMaturity
    ? maxMaturity.reduce((prev, curr) => prev.concat(curr))
    : null;

  const mmpKey = findLastKey(maxMaturity, (item) =>
    item.sum === selectedProduct.firstDrawAmount);

  return mmpKey
    ? (maxMaturity[mmpKey].mmp / 100).toFixed(2)
    : 0;
};

export function normalizeCZSSN(value, previousValue) {
  const parts = value.split('/');

  if (parts.length > 2) {
    return previousValue;
  }

  if (parts.length === 2 && parts[0].length !== 6) {
    return previousValue;
  }

  if (removeNonDigitCharacters(value).length > 10) {
    return previousValue;
  }

  return value.replace(/[^\d/]+/g, '');
}

export const unmask = (value) => value.replace(/_/g, '');

export const shouldShowMissingField = (missingFields, fieldNames) => (
  !missingFields || missingFields.some((item) => fieldNames.includes(item))
);

export const getValueIfPropertyExistsInObject = (obj, prop) => {
  if (typeof obj === 'object' && Object.prototype.hasOwnProperty.call(obj, prop)) {
    return obj[prop];
  }
  return false;
};

export const getIndexStepByName = (stepsArray, stepName) => stepsArray.findIndex(
  (item) => item.type === stepName
);

export const numberWithCommas = (number) => number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const attachLogoutToLink = (ref, logout) => {
  const anchor = ref.current.querySelector('a');
  if (anchor && !anchor.id) {
    anchor.addEventListener('click', (event) => {
      event.preventDefault();
      const redirectLink = anchor.href;
      anchor.className = 'clicked';
      logout({ afterLogoutRedirectLink: redirectLink });
    });
    anchor.id = 'redirect-409';
  }
};

export const addStepToLoanForm = (currentStep, steps, stepObj) => {
  const updatedSteps = [...steps];
  updatedSteps.splice(currentStep + 1, 0, stepObj);

  return updatedSteps;
};

export const isEmptyOrContainsOnlySpaces = (value) => !value || !value.replace(/\s/g, '').length;

export const logoutAndImpersonate = (logout, impersonateOCM, impersonate, getUserSession) => {
  const {
    impersonationKey,
    broker,
    confirmationKey,
    redirectionUrl,
    // eslint-disable-next-line camelcase
    pay_later,
    ocm,
  } = queryStringParser.parse(location.search);
  if (impersonationKey) {
    logout().then(() => {
      if (ocm) {
        impersonateOCM(impersonationKey);
      } else {
        impersonate(
          impersonationKey,
          broker,
          confirmationKey,
          pay_later,
          redirectionUrl
        );
      }
    });
  } else {
    getUserSession();
  }
};
